import { api } from './client';

export interface ClickUpUser {
  id: number;
  username: string;
  email: string;
  color: string;
  profilePicture: string;
  initials: string;
  role: number;
  custom_role: null;
  last_active: string;
  date_joined: string;
  date_invited: string;
}

export interface ClickUpTeam {
  id: string;
  name: string;
  color: string;
  avatar: string | null;
  members: ClickUpUser[];
}

export interface ClickUpSpace {
  id: string;
  name: string;
  private: boolean;
  statuses: ClickUpStatus[];
  multiple_assignees: boolean;
  features: Record<string, any>;
}

export interface ClickUpStatus {
  id: string;
  status: string;
  orderindex: number;
  color: string;
  type: string;
}

export interface ClickUpListStatus {
  status: string;
  orderindex?: number;
  color?: string;
  type?: string;
}

export interface ClickUpList {
  id: string;
  name: string;
  orderindex: number;
  status: string | null;
  priority: string | null;
  assignee: ClickUpUser | null;
  task_count: number;
  due_date: string | null;
  start_date: string | null;
  folder: { id: string; name: string; hidden: boolean; access: boolean } | null;
  space: { id: string; name: string; access: boolean };
  archived: boolean;
  override_statuses: boolean;
  permission_level: string;
}

export interface ClickUpAssignee {
  id: number;
  username: string;
  email: string;
  profilePicture: string;
}

export interface ClickUpTask {
  id: string;
  custom_id: string | null;
  name: string;
  status: string;
  status_color: string;
  priority: number | null;
  assignees: ClickUpAssignee[];
  due_date: string | null;
  start_date: string | null;
  time_spent: number | null;
  url: string;
  list: string | null;
  list_id: string | null;
  folder: string | null;
  space: string | null;
  tags: ClickUpTag[];
  created_at: string;
  updated_at: string;
}

export interface ClickUpTaskDetail extends ClickUpTask {
  description: string;
  status_type: string;
  watchers: ClickUpWatcher[];
  time_estimate: number | null;
  custom_fields: ClickUpCustomField[];
  dependencies: any[];
  linked_tasks: any[];
  creator: {
    id: number;
    username: string;
    email: string;
  } | null;
  subtasks: ClickUpSubtask[];
  checklists: ClickUpChecklist[];
  attachments: ClickUpAttachment[];
  tags: ClickUpTag[];
}

export interface ClickUpCommentUser {
  id: number;
  username?: string;
  email?: string;
  profilePicture?: string;
}

export interface ClickUpTaskComment {
  id: string;
  comment: string;
  date?: string;
  user?: ClickUpCommentUser;
  assigned?: boolean;
  assigned_by?: { id: number; username: string };
  replies?: ClickUpTaskComment[];
}

export interface CommentCreateRequest {
  comment_text: string;
  assignee?: number;
}

export interface CommentUpdateRequest {
  comment_text?: string;
}

export interface CommentReaction {
  id: string;
  comment_id: string;
  reaction: string;
  user: ClickUpUser;
  date: string;
}

export interface ClickUpSubtask extends ClickUpTask {
  parent_id: string;
  orderindex: string;
}

export interface ClickUpChecklist {
  id: string;
  task_id: string;
  name: string;
  orderindex: number;
  position: number;
}

export interface ClickUpChecklistItem {
  id: string;
  checklist_id: string;
  name: string;
  is_resolved: boolean;
  orderindex: number;
}

export interface ClickUpAttachment {
  id: string;
  task_id: string;
  name: string;
  url: string;
  version: number;
  date: string;
  user: ClickUpUser;
  extension: string;
  thumbnail: string | null;
  size: number;
}

export interface ClickUpDependency {
  task_id: string;
  depends_on: string[];
  blocking: string[];
}

export interface ClickUpActivity {
  id: string;
  task_id: string;
  date: string;
  text: string;
  user: ClickUpUser;
  action: string;
}

export interface ClickUpReminder {
  id: string;
  task_id: string;
  date: string;
  user: ClickUpUser;
}

export interface ClickUpTag {
  id: string;
  name: string;
  color: string;
}

export interface ClickUpWatcher {
  id: number;
  username: string;
  email: string;
  profilePicture?: string;
}

export interface ClickUpCustomField {
  id: string;
  name: string;
  type: string;
  value: any;
  enabled: boolean;
  required: boolean;
}

export interface CustomFieldUpdateRequest {
  value: any;
}

export interface TagCreateRequest {
  name: string;
  color?: string;
}

export interface WatcherAddRequest {
  watcher_id: number;
}

export type ClickUpInboxEventType = 'comment' | 'mention';

export interface ClickUpInboxEvent {
  id: string;
  type: ClickUpInboxEventType;
  timestamp: number;
  is_unread: boolean;
  task: ClickUpTask;
  comment: {
    id: string;
    text: string;
    user?: ClickUpCommentUser;
  };
}

export interface ClickUpSettings {
  connected: boolean;
  has_api_key: boolean;
}

export interface ApiKeyRequest {
  api_key: string;
}

export interface TaskUpdateRequest {
  name?: string;
  description?: string;
  status?: string;
  priority?: number;
}

export interface StatusUpdateRequest {
  status: string;
}

export interface TimeEntryRequest {
  duration_minutes: number;
  description?: string;
}

export interface SubtaskCreateRequest {
  name: string;
  description?: string;
  status?: string;
  priority?: number;
  assignees?: number[];
  due_date?: number;
}

export interface SubtaskUpdateRequest {
  name?: string;
  description?: string;
  status?: string;
  priority?: number;
  assignees?: number[];
  due_date?: number;
}

export interface ChecklistCreateRequest {
  name: string;
}

export interface ChecklistItemCreateRequest {
  name: string;
}

export interface ChecklistItemUpdateRequest {
  is_resolved: boolean;
}

export interface ReminderCreateRequest {
  date: number;
}

// API functions
export const clickupApi = {
  // Settings
  getSettings: () => api.get<ClickUpSettings>('/api/v1/clickup/settings').then(res => res.data),
  
  saveApiKey: (apiKey: string) => 
    api.post('/api/v1/clickup/settings/api-key', { api_key: apiKey }).then(res => res.data),
  
  removeApiKey: () => 
    api.delete('/api/v1/clickup/settings/api-key').then(res => res.data),

  // User & Teams
  getUser: () => api.get<{ user: ClickUpUser; connected: boolean }>('/api/v1/clickup/user').then(res => res.data),
  getTeams: () => api.get<{ teams: ClickUpTeam[] }>('/api/v1/clickup/teams').then(res => res.data),
  
  // Spaces & Lists
  getSpaces: (teamId: string) => 
    api.get<{ spaces: ClickUpSpace[] }>(`/api/v1/clickup/teams/${teamId}/spaces`).then(res => res.data),
  
  getLists: (spaceId: string) => 
    api.get<{ lists: ClickUpList[] }>(`/api/v1/clickup/spaces/${spaceId}/lists`).then(res => res.data),

  getListStatuses: (listId: string) =>
    api.get<{ statuses: ClickUpListStatus[]; count: number }>(`/api/v1/clickup/lists/${listId}/statuses`).then(res => res.data),

  // Inbox
  getInbox: (teamId: string, userId?: string) => 
    api.get<{ tasks: ClickUpTask[]; count: number }>(
      `/api/v1/clickup/inbox?team_id=${teamId}${userId ? `&user_id=${userId}` : ''}`
    ).then(res => res.data),

  getInboxEvents: (teamId: string, limitTasks: number = 30, perTaskComments: number = 10) =>
    api.get<{
      events: ClickUpInboxEvent[];
      unread_tasks: number;
      unread_events: number;
      read_state: Record<string, number>;
    }>(
      `/api/v1/clickup/inbox/events?team_id=${teamId}&limit_tasks=${limitTasks}&per_task_comments=${perTaskComments}`
    ).then(res => res.data),

  markInboxTaskRead: (taskId: string, timestampMs?: number) =>
    api.post<{ message: string; task_id: string; timestamp_ms: number }>(
      '/api/v1/clickup/inbox/mark-read',
      { task_id: taskId, timestamp_ms: timestampMs }
    ).then(res => res.data),

  // Tasks
  getListTasks: (listId: string, status?: string, assignee?: string, page: number = 0) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (assignee) params.append('assignee', assignee);
    params.append('page', page.toString());
    return api.get<{ tasks: ClickUpTask[]; count: number }>(
      `/api/v1/clickup/lists/${listId}/tasks?${params.toString()}`
    ).then(res => res.data);
  },

  getTask: (taskId: string) =>
    api.get<{ task: ClickUpTaskDetail }>(`/api/v1/clickup/tasks/${taskId}`).then(res => res.data),

  getTaskComments: (taskId: string) =>
    api.get<{ comments: ClickUpTaskComment[]; count: number }>(`/api/v1/clickup/tasks/${taskId}/comments`).then(res => res.data),

  addTaskComment: (taskId: string, commentText: string, assigneeId?: number) =>
    api.post<{ message: string; result: any }>(
      `/api/v1/clickup/tasks/${taskId}/comments`,
      { comment_text: commentText, assignee: assigneeId }
    ).then(res => res.data),

  updateTaskComment: (commentId: string, data: CommentUpdateRequest) =>
    api.put<{ message: string; result: any }>(
      `/api/v1/clickup/comments/${commentId}`,
      data
    ).then(res => res.data),

  deleteTaskComment: (commentId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/comments/${commentId}`).then(res => res.data),

  addCommentReply: (taskId: string, commentId: string, replyText: string) =>
    api.post<{ message: string; result: any }>(
      `/api/v1/clickup/tasks/${taskId}/comments/${commentId}/replies`,
      { comment_text: replyText }
    ).then(res => res.data),

  addCommentReaction: (commentId: string, reaction: string) =>
    api.post<{ message: string; reaction: CommentReaction }>(
      `/api/v1/clickup/comments/${commentId}/reactions`,
      { reaction }
    ).then(res => res.data),

  removeCommentReaction: (commentId: string, reactionId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/comments/${commentId}/reactions/${reactionId}`).then(res => res.data),

  updateTaskStatus: (taskId: string, status: string) => 
    api.put<{ message: string; task: ClickUpTaskDetail }>(
      `/api/v1/clickup/tasks/${taskId}/status`,
      { status }
    ).then(res => res.data),

  updateTask: (taskId: string, data: TaskUpdateRequest) => 
    api.put<{ message: string; task: ClickUpTaskDetail }>(
      `/api/v1/clickup/tasks/${taskId}`,
      data
    ).then(res => res.data),

  // Time Tracking
  addTimeEntry: (taskId: string, teamId: string, durationMinutes: number, description?: string) => 
    api.post<{ message: string; entry: any }>(
      `/api/v1/clickup/tasks/${taskId}/time?team_id=${teamId}`,
      { duration_minutes: durationMinutes, description: description || '' }
    ).then(res => res.data),

  startTimeTracker: (taskId: string, teamId: string) =>
    api.post<{ message: string; result: any }>(
      `/api/v1/clickup/tasks/${taskId}/time/start?team_id=${teamId}`,
      {}
    ).then(res => res.data),

  stopTimeTracker: (teamId: string) =>
    api.post<{ message: string; result: any }>(
      `/api/v1/clickup/time/stop?team_id=${teamId}`,
      {}
    ).then(res => res.data),

  // Subtasks
  getSubtasks: (taskId: string) =>
    api.get<{ subtasks: ClickUpSubtask[] }>(`/api/v1/clickup/tasks/${taskId}/subtasks`).then(res => res.data),

  createSubtask: (taskId: string, data: SubtaskCreateRequest) =>
    api.post<{ message: string; task: ClickUpSubtask }>(
      `/api/v1/clickup/tasks/${taskId}/subtasks`,
      data
    ).then(res => res.data),

  updateSubtask: (subtaskId: string, data: SubtaskUpdateRequest) =>
    api.put<{ message: string; task: ClickUpSubtask }>(
      `/api/v1/clickup/subtasks/${subtaskId}`,
      data
    ).then(res => res.data),

  deleteSubtask: (subtaskId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/subtasks/${subtaskId}`).then(res => res.data),

  // Checklists
  getChecklists: (taskId: string) =>
    api.get<{ checklists: ClickUpChecklist[] }>(`/api/v1/clickup/tasks/${taskId}/checklists`).then(res => res.data),

  createChecklist: (taskId: string, data: ChecklistCreateRequest) =>
    api.post<{ message: string; checklist: ClickUpChecklist }>(
      `/api/v1/clickup/tasks/${taskId}/checklists`,
      data
    ).then(res => res.data),

  deleteChecklist: (checklistId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/checklists/${checklistId}`).then(res => res.data),

  getChecklistItems: (checklistId: string) =>
    api.get<{ items: ClickUpChecklistItem[] }>(`/api/v1/clickup/checklists/${checklistId}/items`).then(res => res.data),

  createChecklistItem: (checklistId: string, data: ChecklistItemCreateRequest) =>
    api.post<{ message: string; item: ClickUpChecklistItem }>(
      `/api/v1/clickup/checklists/${checklistId}/items`,
      data
    ).then(res => res.data),

  updateChecklistItem: (checklistId: string, itemId: string, data: ChecklistItemUpdateRequest) =>
    api.put<{ message: string; item: ClickUpChecklistItem }>(
      `/api/v1/clickup/checklist-items/${itemId}?checklist_id=${checklistId}`,
      data
    ).then(res => res.data),

  deleteChecklistItem: (checklistId: string, itemId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/checklist-items/${itemId}?checklist_id=${checklistId}`).then(res => res.data),

  // Attachments
  getAttachments: (taskId: string) =>
    api.get<{ attachments: ClickUpAttachment[] }>(`/api/v1/clickup/tasks/${taskId}/attachments`).then(res => res.data),

  uploadAttachment: (taskId: string, file: File) => {
    const formData = new FormData();
    formData.append('attachment', file);
    return api.post<{ message: string; attachment: ClickUpAttachment }>(
      `/api/v1/clickup/tasks/${taskId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ).then(res => res.data);
  },

  deleteAttachment: (attachmentId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/attachments/${attachmentId}`).then(res => res.data),

  // Dependencies
  getDependencies: (taskId: string) =>
    api.get<{ dependencies: ClickUpDependency }>(`/api/v1/clickup/tasks/${taskId}/dependencies`).then(res => res.data),

  addDependency: (taskId: string, dependsOn: string) =>
    api.post<{ message: string; dependencies: ClickUpDependency }>(
      `/api/v1/clickup/tasks/${taskId}/dependencies`,
      { depends_on: dependsOn }
    ).then(res => res.data),

  removeDependency: (taskId: string, dependencyId: string) =>
    api.delete<{ message: string }>(
      `/api/v1/clickup/tasks/${taskId}/dependencies/${dependencyId}`
    ).then(res => res.data),

  // Activity
  getActivity: (taskId: string, page: number = 0) =>
    api.get<{ activities: ClickUpActivity[]; count: number }>(
      `/api/v1/clickup/tasks/${taskId}/activity?page=${page}`
    ).then(res => res.data),

  // Reminders
  getReminders: (taskId: string) =>
    api.get<{ reminders: ClickUpReminder[] }>(`/api/v1/clickup/tasks/${taskId}/reminders`).then(res => res.data),

  createReminder: (taskId: string, data: ReminderCreateRequest) =>
    api.post<{ message: string; reminder: ClickUpReminder }>(
      `/api/v1/clickup/tasks/${taskId}/reminders`,
      data
    ).then(res => res.data),

  deleteReminder: (reminderId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/reminders/${reminderId}`).then(res => res.data),

  // Tags
  getTags: (taskId: string) =>
    api.get<{ tags: ClickUpTag[] }>(`/api/v1/clickup/tasks/${taskId}/tags`).then(res => res.data),

  addTag: (taskId: string, data: TagCreateRequest) =>
    api.post<{ message: string; tag: ClickUpTag }>(
      `/api/v1/clickup/tasks/${taskId}/tags`,
      data
    ).then(res => res.data),

  removeTag: (taskId: string, tagId: string) =>
    api.delete<{ message: string }>(`/api/v1/clickup/tasks/${taskId}/tags/${tagId}`).then(res => res.data),

  // Watchers
  getWatchers: (taskId: string) =>
    api.get<{ watchers: ClickUpWatcher[] }>(`/api/v1/clickup/tasks/${taskId}/watchers`).then(res => res.data),

  addWatcher: (taskId: string, watcherId: number) =>
    api.post<{ message: string; watcher: ClickUpWatcher }>(
      `/api/v1/clickup/tasks/${taskId}/watchers`,
      { watcher_id: watcherId }
    ).then(res => res.data),

  removeWatcher: (taskId: string, watcherId: number) =>
    api.delete<{ message: string }>(`/api/v1/clickup/tasks/${taskId}/watchers/${watcherId}`).then(res => res.data),

  // Custom Fields
  updateCustomField: (taskId: string, fieldId: string, data: CustomFieldUpdateRequest) =>
    api.put<{ message: string; field: ClickUpCustomField }>(
      `/api/v1/clickup/tasks/${taskId}/fields/${fieldId}`,
      data
    ).then(res => res.data),
};
